/** Effective `block.*` namespace preview: flattens the notification's ordered
 *  block/list references (list details fetched per referenced list) and marks
 *  shadowed entries — when two entries resolve to the same effective alias the
 *  LATER one wins in the rendering namespace.
 *
 *  `useEffectiveBlocks` is exported so the ContextSchemaDrawer shows the same
 *  effective entries without duplicating the queries.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Chip, Collapse, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import * as blockListsApi from '@/api/blockLists';
import type { BlockRefItem } from '@/api/notifications';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { blockActions, blockListActions } from '../notificationLinks';

export interface EffectiveBlockRow {
  key: string;
  /** Effective alias in the `block.*` namespace. */
  alias: string;
  /** Provenance: 'direct' or 'list <codename>'. */
  from: string;
  shadowed: boolean;
  /** The referenced list detail hasn't loaded yet. */
  loading?: boolean;
  /** A nested list inside a list (not expanded further). */
  nested?: boolean;
  /** The Notification_Block this row resolves to — the `?edit=` target. Absent
   *  on a still-loading row and on a nested list (which names a list, not a
   *  block). Plumbed through so the preview can link what it is describing
   *  instead of printing an alias that goes nowhere. */
  blockId?: number;
  /** The Block_List this row came from (or IS, for a nested one). */
  listId?: number;
  /** Codename of that list, for the `list=` filter. */
  listCodename?: string;
}

export function useEffectiveBlocks(blockRefs: BlockRefItem[]): {
  rows: EffectiveBlockRow[];
  loading: boolean;
} {
  const listIds = useMemo(
    () => [...new Set(blockRefs.flatMap((r) => (r.block_list != null ? [r.block_list] : [])))],
    [blockRefs],
  );

  const queries = useQueries({
    queries: listIds.map((lid) => ({
      queryKey: ['block-list', lid],
      queryFn: () => blockListsApi.retrieve(lid),
      staleTime: 60_000,
    })),
  });

  // Cheap synchronous flatten — no memo needed (a few dozen rows at most, and
  // `queries` is a fresh array every render anyway).
  const byId = new Map<number, blockListsApi.BlockListDetail>();
  listIds.forEach((lid, i) => {
    const data = queries[i]?.data;
    if (data) byId.set(lid, data);
  });

  const rows: Omit<EffectiveBlockRow, 'shadowed'>[] = [];
  for (const ref of blockRefs) {
    if (ref.block != null) {
      rows.push({
        key: `ref-${ref.id}`,
        alias: ref.alias || ref.block_codename || `#${ref.block}`,
        from: 'direct',
        blockId: ref.block,
      });
    } else if (ref.block_list != null) {
      const detail = byId.get(ref.block_list);
      const listCode = ref.block_list_codename ?? detail?.codename ?? `#${ref.block_list}`;
      if (!detail) {
        rows.push({
          key: `ref-${ref.id}-loading`,
          alias: '…',
          from: `list ${listCode}`,
          loading: true,
          listId: ref.block_list,
          listCodename: listCode,
        });
        continue;
      }
      for (const item of detail.items) {
        if (item.block) {
          rows.push({
            key: `ref-${ref.id}-item-${item.id}`,
            alias: item.alias || item.block.codename,
            from: `list ${listCode}`,
            blockId: item.block.id,
            listId: ref.block_list,
            listCodename: listCode,
          });
        } else if (item.block_list) {
          rows.push({
            key: `ref-${ref.id}-item-${item.id}`,
            alias: item.alias || item.block_list.codename,
            from: `list ${listCode}`,
            nested: true,
            listId: item.block_list.id,
            listCodename: item.block_list.codename,
          });
        }
      }
    }
  }

  // Later duplicate effective-alias wins — flag the earlier occurrences.
  const lastIndex = new Map<string, number>();
  rows.forEach((r, i) => {
    if (!r.loading) lastIndex.set(r.alias, i);
  });
  const withShadow: EffectiveBlockRow[] = rows.map((r, i) => ({
    ...r,
    shadowed: !r.loading && lastIndex.get(r.alias) !== i,
  }));

  return { rows: withShadow, loading: queries.some((q) => q.isLoading) };
}

interface Props {
  blockRefs: BlockRefItem[];
  disableDefaultBlocks: boolean;
}

export function ResolvedBlocksPreview({ blockRefs, disableDefaultBlocks }: Props) {
  const [open, setOpen] = useState(false);
  const { rows } = useEffectiveBlocks(blockRefs);

  return (
    <Box>
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
      >
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        Effective blocks ({rows.length})
      </Link>
      <Collapse in={open}>
        <Stack spacing={0.5} sx={{ mt: 1, pl: 0.5 }}>
          {rows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No referenced blocks.
            </Typography>
          )}
          {rows.map((row) => (
            <Stack key={row.key} direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  ...(row.shadowed ? { textDecoration: 'line-through', color: 'text.disabled' } : {}),
                }}
              >
                {/* The alias is the name the templates use; the LINK is to the
                    block it resolves to, which is what someone reading this
                    list actually wants to open. */}
                {row.blockId != null ? (
                  <ValueActions actions={blockActions({ id: row.blockId, codename: row.alias })}>
                    block.{row.alias}
                  </ValueActions>
                ) : (
                  <>block.{row.alias}</>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                from:{' '}
                {row.listId != null ? (
                  <ValueActions
                    actions={blockListActions({ id: row.listId, codename: row.listCodename ?? null })}
                  >
                    {row.from}
                  </ValueActions>
                ) : (
                  row.from
                )}
              </Typography>
              {row.nested && <Chip size="small" variant="outlined" label="nested list" />}
              {row.shadowed && (
                <Tooltip title="Shadowed by a later block with the same effective alias — the later one wins.">
                  <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />
                </Tooltip>
              )}
            </Stack>
          ))}
          {!disableDefaultBlocks && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              + default block lists (tenant/system config)
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}
